import React, { useCallback, useEffect, useState, useContext } from 'react';
import { FBMOrdersV2Api } from '../../../../api/fbmOrdersV2';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    IconButton,
} from '@mui/material';
import { UserContext } from '../../../../contexts/UserContext';
import { useGetTableData } from '../../hooks/useGetTableData';
import { InfoOutlined } from '@material-ui/icons';
import { FBMOrdersV2Helpers } from './FBMOrdersV2Helpers';

const ChangeFBMOrderLineHaulModal = ({ isOpen, onClose, onSuccess, onError, orderData, shipments }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [selectedLineHaulId, setSelectedLineHaulId] = useState('');
    const [filteredLineHauls, setFilteredLineHauls] = useState([]);
    const { fbmAccount, user } = useContext(UserContext);
    const fbmOrdersV2Api = new FBMOrdersV2Api();
    const fbmOrdersV2Helpers = new FBMOrdersV2Helpers();

    const fetchLineHauls = useCallback(() => {
        let facilityId;
        if (shipments.length > 0) {
            facilityId = shipments[0].facilityId;
        }
        // Only Fedex for now
        return fbmOrdersV2Api.getLineHauls(fbmAccount.fbmAccountName, facilityId, 'fedex');
    }, [fbmAccount.fbmAccountName, shipments]);

    const { data: lineHauls, isLoading: lineHaulsLoading, error: lineHaulsError } = useGetTableData(fetchLineHauls);

    useEffect(() => {
        if (lineHauls) {
            setFilteredLineHauls(fbmOrdersV2Helpers.filterLineHaulsForChangeLineHaulModals(lineHauls));
        }
    }, [lineHauls]);

    useEffect(() => {
        if (!(onSuccess instanceof Function)) {
            console.error('ChangeFBMOrderLineHaulModal intialized incorrectly onSuccess is not a function');
        }
        if (!(onError instanceof Function)) {
            console.error('ChangeFBMOrderLineHaulModal intialized incorrectly onError is not a function');
        }
        if (!(onClose instanceof Function)) {
            console.error('ChangeFBMOrderLineHaulModal intialized incorrectly onClose is not a function');
        }
    }, [onClose, onSuccess, onError]);

    const handleClose = () => {
        onClose();
    };

    const handleChangeLineHaul = useCallback(async () => {
        setIsLoading(true);
        const response = await fbmOrdersV2Api.changeOrderLineHaul(
            orderData.id,
            fbmAccount.fbmAccountName,
            selectedLineHaulId
        );
        if (response.status) {
            onSuccess(`Order ${orderData.id} line haul changed successfully`);
            handleClose();
        } else {
            onError(response.msg || 'Failed to change line haul');
        }
        setIsLoading(false);
    }, [fbmAccount.fbmAccountName, fbmOrdersV2Api, handleClose, onError, onSuccess, orderData?.id, selectedLineHaulId]);

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            disableScrollLock={true}
            hideBackdrop={false}
        >
            <DialogTitle>Change Order Line Haul</DialogTitle>
            <DialogContent>
                {orderData && (
                    <div className="mb-6">
                        <Typography variant="subtitle1" className="mb-4">
                            <strong>Order ID:</strong> {orderData.orderId}
                        </Typography>
                        <div className="overflow-hidden rounded-lg border border-gray-200">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Shipment ID
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Carrier
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {shipments?.map((shipment) => (
                                        <tr
                                            key={shipment.shipmentId}
                                            className="hover:bg-gray-50 transition-colors duration-150 ease-in-out"
                                        >
                                            <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                                {shipment.shipmentId}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                                {shipment.carrier}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                    {shipment.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <InputLabel>Line Haul</InputLabel>
                    <Tooltip title="Format: Carrier - Hub - Arrival Day" placement="right" arrow>
                        <IconButton size="small">
                            <InfoOutlined fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </div>
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <Select
                        value={selectedLineHaulId}
                        onChange={(e) => setSelectedLineHaulId(e.target.value)}
                        label="Line Haul"
                    >
                        {Array.isArray(filteredLineHauls) &&
                            filteredLineHauls.map((lineHaul) => (
                                <MenuItem key={lineHaul.id} value={lineHaul.id}>
                                    {lineHaul.carrier} - {lineHaul.hub} - {lineHaul.arrivalDay}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="secondary">
                    Cancel
                </Button>
                <Button onClick={handleChangeLineHaul} color="primary" variant="contained" disabled={isLoading}>
                    {isLoading ? 'Changing...' : 'Confirm Line Haul Change'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { ChangeFBMOrderLineHaulModal };
